import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  Checkbox,
  useMediaQuery,
  useTheme,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateMoveService } from "../../redux/action/move-service";
import { unwrapResult } from "@reduxjs/toolkit";
import { getRate } from "../../redux/action/rate";
import Header from "../header/Header";
import { getCarrierByBusinessName } from "../../redux/action/carrier";
import StepProgress from "../stepsProgress/stepsProgress";

interface BulkyItem {
  name: string;
  fee: number;
}

const MovingItems = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [bulkyItems, setBulkyItems] = useState<BulkyItem[]>([]);
  const [carrier, setCarrier] = useState<any>(null);
  const { canonicalId } = useParams();
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const carrierId = localStorage.getItem("CarrierId");
  const navigate = useNavigate();
  const { businessName, carrierCanonicalId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const lastStep = 4;
  const handleItemChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    setSelectedItems((prevSelectedItems) => {
      if (checked) {
        return [...prevSelectedItems, value];
      } else {
        return prevSelectedItems.filter((item) => item !== value);
      }
    });
  };
  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res: any) => {});
    };

    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);
  const handleContinue = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage("Saving your bulky items selection...");

      const itemsWithPrices = bulkyItems
        .filter((item) => selectedItems.includes(item.name))
        .map((item) => ({
          name: item.name,
          fee: item.fee,
        }));

      const bulkyItemPrice = itemsWithPrices.reduce(
        (sum, item) => sum + item.fee,
        0
      );

      const moveServiceData = {
        canonical_id: canonicalId,
        items: JSON.stringify(itemsWithPrices),
      };

      console.log(moveServiceData.items, "ITEMS");

      setTimeout(async () => {
        try {
          const res = await dispatch<any>(updateMoveService(moveServiceData));
          unwrapResult(res);
          const offersInsurance = carrier?.insurance != null;
          setLoadingMessage(
            offersInsurance
              ? "Redirecting to insurance page..."
              : "Redirecting to price calculation..."
          );
          if (offersInsurance) {
            navigate(
              `/${businessName}/${carrierCanonicalId}/insurance/${canonicalId}`
            );
          } else {
            navigate(
              `/${businessName}/${carrierCanonicalId}/move-price/${canonicalId}`
            );
          }
        } catch (err: any) {
          setError(err?.message || "Failed to update move service");
        } finally {
          setIsLoading(false);
        }
      }, 2000);
    } catch (err) {
      setIsLoading(false);
      setError("An unexpected error occurred");
    }
  };

  useEffect(() => {
    if (businessName && bulkyItems.length === 0) {
      setIsLoading(true);
      setLoadingMessage("Loading bulky items...");
      dispatch<any>(getCarrierByBusinessName(businessName))
        .then(unwrapResult)
        .then((res: any) => {
          setCarrier(res);
          if (res.rate.bulky_fee_list) {
            const items = Object.entries(res.rate.bulky_fee_list).map(
              ([name, fee]) => ({
                name,
                fee: Number(fee),
              })
            );
            setBulkyItems(items);
          }
        })
        .catch((err: any) => {
          console.log(err, "error occur");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [businessName]);
  const handleBack = () => {
    navigate(-1);
  };
  return (
    <div>
      <Header />
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}
      <StepProgress currentStep={4} />

      <Container sx={{ maxWidth: "800px", py: 4 }}>
        {/* Title */}
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700}>
            Select Bulky Items
          </Typography>
          <Typography color="text.secondary" fontSize={14}>
            Following items are considered as bulky by your carrier. If you are
            moving them along, please select them.
          </Typography>
        </Box>

        {/* Bulky list */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            mb: 3,
          }}
        >
          {bulkyItems.map((item, index) => {
            const checked = selectedItems.includes(item.name);

            return (
              <Box
                key={item.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2.5,
                  borderBottom:
                    index !== bulkyItems.length - 1
                      ? "1px solid #e2e8f0"
                      : "none",
                  background: checked ? "rgba(59,130,246,0.10)" : "#fff",
                  transition: "0.2s",
                  "&:hover": { background: "rgba(59,130,246,0.15)" },
                }}
              >
                {/* Left */}
                <Box display="flex" alignItems="center" gap={1.5}>
                  {/* mimic checkbox border UI behind real checkbox */}
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "4px",
                      border: checked
                        ? "2px solid #3b82f6"
                        : "2px solid #9ca3af",
                      background: checked ? "#3b82f6" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      color: "#fff",
                    }}
                  >
                    {checked && "✓"}
                  </Box>

                  {/* Hidden real checkbox to preserve your logic */}
                  <Checkbox
                    checked={checked}
                    onChange={handleItemChange}
                    value={item.name}
                    sx={{ position: "absolute", opacity: 0, cursor: "pointer" }}
                  />

                  {/* Icon + Text */}
                  <Typography fontSize={15}>📦 {item.name}</Typography>
                </Box>

                <Typography fontSize={13} color="text.secondary">
                  Fee: ${item.fee}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Summary */}
        {selectedItems.length > 0 && (
          <Box
            sx={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "12px",
              p: 2.5,
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
            }}
          >
            <Typography>{selectedItems.length} Items</Typography>
            <Typography sx={{ color: "#2563eb", fontSize: "18px" }}>
              $
              {selectedItems.reduce(
                (sum, name) =>
                  sum + (bulkyItems.find((i) => i.name === name)?.fee || 0),
                0
              )}
            </Typography>
          </Box>
        )}

        {/* Tip */}
        <Box
          sx={{
            background: "#f8fafc",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            p: 2,
            mb: 3,
            textAlign: "center",
          }}
        >
          <Typography fontSize={14}>
            💡 Bulky items require special handling and equipment. Fees help
            cover the resources needed for safe transport.
          </Typography>
        </Box>

        {/* Buttons */}
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="text"
            onClick={handleBack}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={isLoading}
            sx={{ textTransform: "none", px: 4 }}
          >
            Continue →
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default MovingItems;
